import type {
  PostingCategoryKind,
  PostingExprNode,
  PostingNode,
} from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import {
  buildTreeFilterNodes,
  buildValNode,
  wrapExprNode,
} from './node-builders';
import { PostingDiscoveryTags } from './tags';

const RICS_LEVEL_ID_TO_KIND: Record<string, PostingCategoryKind> = {
  company: PostingDiscoveryTags.CompanyId,
  rics_k10: PostingDiscoveryTags.RicsK10,
  rics_k50: PostingDiscoveryTags.RicsK50,
  rics_k400: PostingDiscoveryTags.RicsK400,
};

/** Shared by the Company and Industry filters — both sit on the RICS tree. */
export const makeRicsLeaf = (
  id: number,
  levelId: string
): PostingExprNode | null => {
  const kind = RICS_LEVEL_ID_TO_KIND[levelId];
  return kind ? buildValNode(kind, id) : null;
};

export const buildCompanyFilter = (filters: Filter[]): PostingNode | null => {
  const companyFilter = findFilterById(filters, FilterId.COMPANY);
  if (!companyFilter || companyFilter.type !== FilterType.TREE) {
    return null;
  }

  const exprNode = buildTreeFilterNodes(companyFilter, makeRicsLeaf);
  if (!exprNode) return null;

  return wrapExprNode(exprNode, companyFilter.isNot);
};

export const buildMultiCompanyFilter = (
  filters: Filter[]
): PostingNode | PostingNode[] | null => {
  const multiCompany = findFilterById(filters, FilterId.MULTI_COMPANY);
  if (!multiCompany || !isMultiFilter(multiCompany)) {
    return null;
  }
  if (multiCompany.instances.length === 0) {
    return null;
  }

  const instanceNodes = multiCompany.instances
    .filter((inst): inst is TreeFilter => inst?.type === FilterType.TREE)
    .map((instance) => {
      const exprNode = buildTreeFilterNodes(instance, makeRicsLeaf);
      if (!exprNode) return null;
      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is PostingNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }
  return instanceNodes;
};
